import { useState, useEffect, useCallback } from 'react';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

type PhotoNode = {
    readonly serviceId: string | null;
    readonly ' $fragmentSpreads': $TSFixMe;
};

type PhotoEdges =
    | readonly ({
          readonly node: PhotoNode | null;
      } | null)[]
    | null;

type PhotoNodes = readonly (PhotoNode | null)[];

type PhotoGrid = PhotoNode[][];

const shouldShowPhoto = (photoId: string | null, mainPhotoId?: string): boolean => {
    return photoId !== mainPhotoId;
};

export const useGeneratePhotoGrid = ({
    columnsPerPage,
    photoEdges,
    photoNodes,
    mainPhotoId,
}: {
    columnsPerPage: number;
    photoEdges?: PhotoEdges;
    photoNodes?: PhotoNodes;
    mainPhotoId?: string;
}): { photoGrid: PhotoGrid } => {
    const generatePhotoGrid = useCallback(
        ({ edges, nodes }: { edges?: PhotoEdges; nodes?: PhotoNodes }): PhotoGrid => {
            const getGridColumn = (grid: PhotoGrid, index: number): PhotoNode[] => {
                const column = index % columnsPerPage;
                if (!grid[column]) {
                    grid[column] = [];
                }
                return grid[column];
            };

            const grid: PhotoGrid = Array.from({ length: columnsPerPage }, () => []);

            if (nodes) {
                (nodes || []).filter(filterNulls).forEach((photo, index) => {
                    const col = getGridColumn(grid, index);
                    if (shouldShowPhoto(photo.serviceId, mainPhotoId)) {
                        col.push(photo);
                    }
                });
            } else if (edges) {
                (edges || []).filter(filterNulls).forEach((photo, index) => {
                    const col = getGridColumn(grid, index);
                    if (photo.node && shouldShowPhoto(photo.node.serviceId, mainPhotoId)) {
                        col.push(photo.node);
                    }
                });
            }

            return grid;
        },
        [columnsPerPage, mainPhotoId]
    );

    const [photoGrid, setPhotoGrid] = useState<PhotoGrid>(() =>
        generatePhotoGrid({ edges: photoEdges, nodes: photoNodes })
    );

    useEffect(() => {
        setPhotoGrid(generatePhotoGrid({ edges: photoEdges, nodes: photoNodes }));
    }, [generatePhotoGrid, photoEdges, photoNodes]);

    return { photoGrid };
};
